import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Info, User, PenLine } from 'lucide-react';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Template, templatesService } from '@/services/templates.service';

/** Contact fields a campaign variable can read from. */
export const CONTACT_FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: 'firstName', label: 'First name' },
  { value: 'lastName',  label: 'Last name' },
  { value: 'fullName',  label: 'Full name' },
  { value: 'phone',     label: 'Phone number' },
  { value: 'email',     label: 'Email' },
  { value: 'company',   label: 'Company' },
  { value: 'jobTitle',  label: 'Job title' },
  { value: 'city',      label: 'City' },
  { value: 'country',   label: 'Country' },
];

/** Best-guess default source for a variable name ("firstName" → contact.firstName, else manual). */
export function defaultMappingFor(varName: string): string {
  const k = varName.toLowerCase().replace(/[_\s]/g, '');
  const hit = CONTACT_FIELD_OPTIONS.find((o) => o.value.toLowerCase() === k);
  if (hit) return hit.value;
  if (k === 'name' || k === 'customername' || k === 'fullname') return 'fullName';
  return 'manual';
}

interface Props {
  template: Template;
  /** Map of var name → override value used when contact field is empty. */
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  /** When true, show contact-source hint below each variable. */
  showContactHints?: boolean;
  /** Per-variable source ('manual' or a contact field key). When provided
      together with onMappingsChange, each row gets a source picker. */
  mappings?: Record<string, string>;
  onMappingsChange?: (next: Record<string, string>) => void;
}

/**
 * Extracts every {{var}} reference from the template body, header text,
 * and URL/copy-code buttons — preserving order of first appearance.
 */
export function extractTemplateVariables(t: Template): string[] {
  const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  const seen = new Set<string>();
  const out: string[] = [];

  const scan = (text?: string, map: Record<string, string> = {}) => {
    if (!text) return;
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const name = /^\d+$/.test(m[1]) ? map[m[1]] ?? `parameter_${m[1]}` : m[1];
      if (!seen.has(name)) { seen.add(name); out.push(name); }
    }
  };

  for (const name of [...Object.values(t.headerVariableMap ?? {}), ...Object.values(t.variableMap ?? {})]) {
    if (!seen.has(name)) { seen.add(name); out.push(name); }
  }
  scan(t.headerText, t.headerVariableMap);
  scan(t.body, t.variableMap);
  for (const b of t.buttons ?? []) {
    scan(b.url);
    scan(b.text);
  }
  // Also walk per-card bodies + buttons (carousel)
  for (const c of t.cards ?? []) {
    scan(c.body);
    for (const b of c.buttons ?? []) {
      scan(b.url);
      scan(b.text);
    }
  }
  return out;
}

/**
 * Renders a row of inputs for each variable in a template. The values
 * are used as both the live preview substitution AND the per-campaign
 * override map sent to the backend.
 */
export const TemplateVariablesPanel: React.FC<Props> = ({ template, values, onChange, showContactHints, mappings, onMappingsChange }) => {
  const vars = useMemo(() => extractTemplateVariables(template), [template]);
  const [contactFields, setContactFields] = useState(CONTACT_FIELD_OPTIONS);
  useEffect(() => {
    let active = true;
    if (onMappingsChange) templatesService.variables().then(keys => {
      if (active) setContactFields([...CONTACT_FIELD_OPTIONS, ...keys.filter(key => !CONTACT_FIELD_OPTIONS.some(field => field.value === key)).map(key => ({ value: key, label: key === 'whatsappPhone' ? 'WhatsApp number' : key }))]);
    }).catch(() => {});
    return () => { active = false; };
  }, [onMappingsChange]);

  if (vars.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-dashed border-border bg-surface-alt/40 px-3 py-2.5 text-xs text-text-muted">
        <Info size={13} className="shrink-0" />
        This template has no variables — nothing to fill in.
      </div>
    );
  }

  const set = (key: string, val: string) => {
    onChange({ ...values, [key]: val });
  };
  const setMapping = (key: string, source: string) => {
    onMappingsChange?.({ ...(mappings ?? {}), [key]: source });
  };
  const withPicker = !!onMappingsChange;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-text-secondary">
        <Sparkles size={13} className="text-primary" />
        <span>
          {vars.length} variable{vars.length === 1 ? '' : 's'} —
          {withPicker
            ? ' pick where each value comes from: a contact field, or a manual value.'
            : ' values fall back to these when a contact field is empty.'}
        </span>
      </div>

      <div className="space-y-2.5">
        {vars.map((name) => {
          const sample = (template.previewSamples ?? {})[name];
          const source = (mappings ?? {})[name] ?? defaultMappingFor(name);
          const isManual = source === 'manual';
          const fieldLabel = contactFields.find((o) => o.value === source)?.label ?? source;

          if (!withPicker) {
            const helper = showContactHints
              ? `Reads from contact.${name}; falls back to value below.`
              : sample
                ? `Sample: ${sample}`
                : undefined;
            return (
              <FormField key={name} label={`{{${name}}}`} hint={helper}>
                <Input
                  value={values[name] ?? ''}
                  placeholder={sample || `Default value for {{${name}}}`}
                  onChange={(e) => set(name, e.target.value)}
                />
              </FormField>
            );
          }

          return (
            <div key={name} className="rounded-xl border border-border bg-surface-alt/40 p-3">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-soft px-1.5 py-0.5 font-mono text-xs font-semibold text-primary">{`{{${name}}}`}</span>
                <span className="inline-flex items-center gap-1 text-2xs text-text-muted">
                  {isManual ? <PenLine size={11} /> : <User size={11} />}
                  {isManual ? 'Same text for every recipient' : `Personalized from each contact's ${fieldLabel.toLowerCase()}`}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[190px_1fr]">
                <Select value={source} onChange={(e) => setMapping(name, e.target.value)}>
                  <option value="manual">Manual value</option>
                  <optgroup label="From contact">
                    {contactFields.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </optgroup>
                </Select>
                <Input
                  value={values[name] ?? ''}
                  placeholder={isManual
                    ? (sample || `Text sent to everyone for {{${name}}}`)
                    : `Fallback if the contact has no ${fieldLabel.toLowerCase()}`}
                  onChange={(e) => set(name, e.target.value)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
